import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    "domainInput", 
    "domainError", 
    "validateButton", 
    "activateButton", 
    "saveButton", 
    "dnsStatus", 
    "dnsMessage",
    "ipAddress", 
    "lastChecked"
  ]

  connect() {
    super.connect()
    // Remembers the already-DNS-verified (saved) domain so re-typing it needs no re-check.
    this.savedDomain = this.hasDomainInputTarget ? this.domainInputTarget.value.trim() : ""
    if (this.hasDomainInputTarget) {
      this.validateDomain()
    }
  }

  // Format-only check -- saving still requires a passing DNS check (see checkDNS).
  validateDomain() {
    if (!this.hasDomainInputTarget) return false
    const domain = this.domainInputTarget.value.trim()
    const errorElement = this.hasDomainErrorTarget ? this.domainErrorTarget : null
    const saveButton = this.hasSaveButtonTarget ? this.saveButtonTarget : null

    if (!domain) {
      if (errorElement) errorElement.textContent = ""
      if (saveButton) saveButton.disabled = true
      return false
    }

    const domainParts = domain.split('.')

    if (domainParts.length > 3 || domainParts.length < 2) {
      this.setError("Domain must have 2 or 3 levels (e.g., example.com or donate.example.com)", errorElement, saveButton)
      return false
    }

    const domainRegex = /^[a-zA-Z0-9.-]+$/
    if (!domainRegex.test(domain) || domain.match(/\.\./) || domain.match(/^[.-]|[.-]$/)) {
      this.setError("Invalid domain format", errorElement, saveButton)
      return false
    }

    for (const part of domainParts) {
      if (part.length === 0 || part.length > 63 || part.match(/^-|-$/)) {
        this.setError("Invalid domain part length or hyphen placement", errorElement, saveButton)
        return false
      }
    }

    const alreadyVerified = domain === this.savedDomain
    if (saveButton) saveButton.disabled = !alreadyVerified

    if (errorElement) {
      if (alreadyVerified) {
        errorElement.textContent = "Valid domain format"
        errorElement.className = "text-success"
      } else {
        errorElement.textContent = 'Valid format -- click "Check on DNS" to verify before saving'
        errorElement.className = "text-muted"
      }
    }
    return true
  }

  setError(message, errorElement, saveButton) {
    if (errorElement) {
      errorElement.textContent = message
      errorElement.className = "text-danger"
    }
    if (saveButton) saveButton.disabled = true
  }

  async checkDNS(event) {
    event.preventDefault()
    const button = event.currentTarget
    const originalText = button.textContent
    button.disabled = true
    button.textContent = "Checking DNS..."

    let domainValue = this.hasDomainInputTarget ? this.domainInputTarget.value.trim() : null

    try {
      const body = domainValue ? { custom_domain: { domain: domainValue } } : {}

      const response = await fetch(button.dataset.checkDnsPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success) {
        // Update notification alert box
        if (this.hasDnsMessageTarget) {
          this.dnsMessageTarget.innerHTML = data.resolving
            ? `<div class="alert alert-success mb-0"><strong>DNS is resolving correctly!</strong> Domain is pointing to: ${data.resolved_ip}</div>`
            : `<div class="alert alert-warning mb-0"><strong>DNS is not resolving correctly</strong>, Expected IP: ${data.expected_ip}, Current IP: ${data.resolved_ip || 'Not resolving'}</div>`
        }

        // Update list status indicators -- same markup the server renders, so the
        // badge doesn't visibly change shape/style once JS takes over after a live check.
        if (this.hasIpAddressTarget) {
          this.ipAddressTarget.textContent = data.resolved_ip || 'NOT RESOLVING'
          this.ipAddressTarget.classList.toggle('status-badge-secondary', data.resolving)
          this.ipAddressTarget.classList.toggle('status-badge-neutral', !data.resolving)
        }

        if (this.hasDnsStatusTarget) {
          this.dnsStatusTarget.innerHTML = data.resolving
            ? '<span class="ui-icon ui-icon-secondary"><i class="fas fa-check"></i></span>'
            : '<span class="ui-icon ui-icon-primary"><i class="fas fa-times"></i></span>'
        }

        if (this.hasLastCheckedTarget && data.last_checked) {
          this.lastCheckedTarget.textContent = data.last_checked
        }

        // Only unlock "Update Domain" once the server confirms this domain resolves.
        if (data.resolving && this.hasDomainInputTarget) {
          this.savedDomain = this.domainInputTarget.value.trim()
        }
        if (this.hasSaveButtonTarget) {
          this.saveButtonTarget.disabled = !data.resolving
        }
        if (this.hasDomainErrorTarget) {
          this.domainErrorTarget.textContent = data.resolving ? "Valid domain format" : "Domain is not resolving yet -- fix your DNS and check again before saving"
          this.domainErrorTarget.className = data.resolving ? "text-success" : "text-danger"
        }

        if (this.hasActivateButtonTarget) {
          this.activateButtonTarget.disabled = !data.resolving
        }
      } else if (this.hasDnsMessageTarget) {
        this.dnsMessageTarget.innerHTML = `<div class="alert alert-danger mb-0"><strong>Error checking DNS:</strong> ${data.error || 'Unexpected error'}</div>`
      }
    } catch (error) {
      if (this.hasDnsMessageTarget) {
        this.dnsMessageTarget.innerHTML = `<div class="alert alert-danger mb-0"><strong>Error checking DNS:</strong> ${error.message}</div>`
      }
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }
}